import { useEffect, useMemo, useState } from "react";
import { AppShell, PageHeader } from "../components/AppShell";
import { IngredientPicker } from "../components/IngredientPicker";
import { UnitPills } from "../components/UnitPills";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { OcrPurchaseDialog } from "../components/OcrPurchaseDialog";
import { QuotaBadge } from "../components/QuotaBadge";
import { api, formatApiError } from "../lib/api";
import { catColor, euro, euroPrecise } from "../lib/constants";
import { computePurchasePreview } from "../lib/calc";
import { Plus, Trash2, ShoppingBasket, ChevronRight, Sparkles } from "lucide-react";

export default function Purchases() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ingredients, setIngredients] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [ocrOpen, setOcrOpen] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/purchases")
            .then(({ data }) => setItems(data))
            .finally(() => setLoading(false));
    };

    const reloadAll = () => {
        load();
        api.get("/ingredients").then(({ data }) => setIngredients(data));
    };

    useEffect(() => {
        load();
        api.get("/ingredients").then(({ data }) => setIngredients(data));
    }, []);

    const del = async (id) => {
        if (!window.confirm("Eliminare questo acquisto?")) return;
        try {
            await api.delete(`/purchases/${id}`);
            load();
        } catch (e) {
            alert(formatApiError(e));
        }
    };

    return (
        <AppShell>
            <PageHeader
                eyebrow="Magazzino"
                title="Acquisti"
                subtitle="Registra le fatture fornitori, il costo medio si aggiorna automaticamente."
                right={
                    <div className="hidden lg:flex items-center gap-2">
                        <QuotaBadge kind="invoices" label="Fatture AI" />
                        <button
                            onClick={() => setOcrOpen(true)}
                            data-testid="ocr-import-btn"
                            className="inline-flex items-center gap-2 rounded-full border border-brand-gold text-brand-navy bg-brand-gold/10 px-4 py-2.5 text-sm font-semibold hover:bg-brand-gold/20 transition"
                        >
                            <Sparkles size={16} /> Importa fattura
                        </button>
                        <button
                            onClick={() => setModalOpen(true)}
                            data-testid="new-purchase-btn"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-navyLight transition shadow-card"
                        >
                            <Plus size={16} /> Nuovo acquisto
                        </button>
                    </div>
                }
            />

            {loading ? (
                <div className="py-12 text-center text-sm text-brand-muted">Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="bg-paper rounded-2xl border border-dashed border-brand-line p-12 text-center">
                    <ShoppingBasket className="mx-auto text-brand-muted mb-3" size={40} strokeWidth={1.4} />
                    <div className="font-display text-2xl text-brand-ink">Nessun acquisto</div>
                    <div className="mt-1 text-sm text-brand-muted">
                        Registra un acquisto per aggiornare il costo degli ingredienti.
                    </div>
                </div>
            ) : (
                <div className="bg-paper rounded-xl border border-brand-line shadow-card overflow-hidden" data-testid="purchases-list">
                    {items.map((p, i) => (
                        <div
                            key={p.id}
                            className={`flex items-center gap-4 px-4 sm:px-5 py-4 ${i > 0 ? "border-t border-brand-line" : ""}`}
                            data-testid={`purchase-row-${p.id}`}
                        >
                            <span
                                className="h-12 w-1.5 rounded-full shrink-0"
                                style={{ background: catColor(p.ingredient_category) }}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                    <div className="text-sm font-semibold text-brand-ink truncate">
                                        {p.ingredient_name || "—"}
                                    </div>
                                    <div className="text-[11px] text-brand-muted">
                                        {p.quantity} {p.unit} @ {euroPrecise(p.cost_per_unit)}/{p.unit}
                                    </div>
                                </div>
                                <div className="text-[11px] uppercase tracking-wider text-brand-muted mt-0.5">
                                    {p.date}{p.supplier ? ` · ${p.supplier}` : ""}
                                </div>
                            </div>
                            <div className="font-display text-xl text-brand-ink">{euro(p.total_price)}</div>
                            <button
                                onClick={() => del(p.id)}
                                data-testid={`purchase-delete-${p.id}`}
                                className="text-brand-muted hover:text-brand-danger p-2 transition"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Mobile actions */}
            <div className="lg:hidden fixed right-5 bottom-24 z-40 flex flex-col gap-2.5 items-end">
                <button
                    onClick={() => setOcrOpen(true)}
                    data-testid="purchases-ocr-fab"
                    aria-label="Importa fattura"
                    className="h-12 w-12 rounded-full bg-brand-navy text-brand-gold shadow-card flex items-center justify-center"
                >
                    <Sparkles size={20} />
                </button>
                <button
                    onClick={() => setModalOpen(true)}
                    data-testid="purchases-fab"
                    aria-label="Nuovo acquisto"
                    className="h-14 w-14 rounded-full bg-brand-gold text-brand-navy shadow-gold flex items-center justify-center"
                >
                    <Plus size={24} strokeWidth={2.6} />
                </button>
            </div>

            <NewPurchaseModal
                open={modalOpen}
                onOpenChange={setModalOpen}
                ingredients={ingredients}
                onCreated={reloadAll}
            />

            <OcrPurchaseDialog
                open={ocrOpen}
                onOpenChange={setOcrOpen}
                onImported={reloadAll}
            />
        </AppShell>
    );
}

function NewPurchaseModal({ open, onOpenChange, ingredients, onCreated }) {
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({
        ingredient_id: "",
        supplier: "",
        date: today,
        quantity: 1,
        unit: "kg",
        total_price: 0,
    });
    const [pickerOpen, setPickerOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [suppliers, setSuppliers] = useState([]);
    const [supplierMode, setSupplierMode] = useState("preferred"); // 'preferred' | 'manual'

    useEffect(() => {
        if (open) {
            setForm({ ingredient_id: "", supplier: "", date: today, quantity: 1, unit: "kg", total_price: 0 });
            setSelected(null);
            setError("");
            setSupplierMode("preferred");
            api.get("/suppliers")
                .then((r) => setSuppliers(r.data || []))
                .catch(() => setSuppliers([]));
        }
    }, [open, today]);

    // Lista da mostrare nel dropdown: preferiti se ce ne sono, altrimenti tutti
    const preferred = suppliers.filter((s) => s.is_preferred);
    const dropdownList = preferred.length > 0 ? preferred : suppliers;

    const preview = useMemo(() => {
        if (!selected) return null;
        return computePurchasePreview({
            quantity: form.quantity,
            unit: form.unit,
            total_price: form.total_price,
            ingredient: selected,
        });
    }, [form, selected]);

    const save = async () => {
        setError("");
        if (!selected) { setError("Seleziona un ingrediente."); return; }
        if (!(Number(form.quantity) > 0)) { setError("Quantità non valida."); return; }
        if (!(Number(form.total_price) >= 0)) { setError("Prezzo non valido."); return; }
        setSaving(true);
        try {
            await api.post("/purchases", {
                ingredient_id: selected.id,
                supplier: form.supplier,
                date: form.date,
                quantity: Number(form.quantity),
                unit: form.unit,
                total_price: Number(form.total_price),
            });
            onCreated();
            onOpenChange(false);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-lg bg-white p-0 overflow-hidden" data-testid="new-purchase-modal">
                    <DialogHeader className="px-6 pt-6 pb-3 border-b border-brand-line">
                        <DialogTitle className="font-display text-2xl text-brand-ink">Nuovo acquisto</DialogTitle>
                        <DialogDescription className="text-sm text-brand-muted">
                            Registra una fattura fornitore: il costo medio ponderato dell'ingrediente verrà aggiornato.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="p-6 space-y-4">
                        {error && (
                            <div className="rounded-lg bg-red-50 border border-red-200 text-brand-danger text-sm px-4 py-2.5" data-testid="purchase-error">
                                {error}
                            </div>
                        )}

                        <div>
                            <Label>Ingrediente</Label>
                            <button
                                type="button"
                                onClick={() => setPickerOpen(true)}
                                data-testid="purchase-ingredient-picker"
                                className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-brand-cream border border-brand-line text-left hover:border-brand-gold transition"
                            >
                                {selected ? (
                                    <div>
                                        <div className="text-sm font-semibold text-brand-ink">{selected.name}</div>
                                        <div className="text-[11px] text-brand-muted">{euroPrecise(selected.cost_per_unit)}/{selected.unit}</div>
                                    </div>
                                ) : (
                                    <div className="text-sm text-brand-muted">Scegli un ingrediente…</div>
                                )}
                                <ChevronRight size={16} className="text-brand-muted" />
                            </button>
                        </div>

                        <div className="grid grid-cols-[1fr_auto] gap-3">
                            <div>
                                <Label>Quantità</Label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.quantity}
                                    onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                                    data-testid="purchase-qty"
                                    className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold"
                                />
                            </div>
                            <div>
                                <Label>&nbsp;</Label>
                                <UnitPills
                                    value={form.unit}
                                    onChange={(u) => setForm((f) => ({ ...f, unit: u }))}
                                    testid="purchase-unit-pills"
                                />
                            </div>
                        </div>

                        <div>
                            <Label>Prezzo totale (€)</Label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.total_price}
                                onChange={(e) => setForm((f) => ({ ...f, total_price: e.target.value }))}
                                data-testid="purchase-total-price"
                                className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold"
                            />
                        </div>

                        {preview && (
                            <div
                                className="rounded-lg px-4 py-3"
                                style={{ background: "rgba(201,169,97,0.12)", border: "1px solid rgba(201,169,97,0.35)" }}
                                data-testid="purchase-preview"
                            >
                                <div className="text-[10px] uppercase tracking-[0.16em] text-brand-goldDark font-bold">
                                    Nuovo costo unitario
                                </div>
                                <div className="mt-1 font-display text-2xl text-brand-navy">
                                    {euroPrecise(preview.new_cost)} / {preview.base_unit}
                                </div>
                                <div className="text-[11px] text-brand-muted mt-1">
                                    Il costo medio ponderato dell'ingrediente verrà aggiornato automaticamente.
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <div className="flex items-center justify-between gap-2 mb-1.5">
                                    <Label>Fornitore</Label>
                                    {dropdownList.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setSupplierMode(supplierMode === "preferred" ? "manual" : "preferred")}
                                            data-testid="toggle-supplier-mode"
                                            className="text-[11px] text-brand-muted hover:text-brand-navy underline"
                                        >
                                            {supplierMode === "preferred" ? "scrivi manualmente" : "scegli dalla lista"}
                                        </button>
                                    )}
                                </div>
                                {supplierMode === "preferred" && dropdownList.length > 0 ? (
                                    <select
                                        value={form.supplier}
                                        onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))}
                                        data-testid="purchase-supplier-select"
                                        className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold appearance-none cursor-pointer"
                                    >
                                        <option value="">— Nessuno —</option>
                                        {preferred.length > 0 && (
                                            <optgroup label="★ Preferiti">
                                                {preferred.map((s) => (
                                                    <option key={s.id} value={s.name}>{s.name}{s.city ? ` · ${s.city}` : ""}</option>
                                                ))}
                                            </optgroup>
                                        )}
                                        {preferred.length === 0 && suppliers.length > 0 && (
                                            <optgroup label="Tutti i fornitori del portale">
                                                {suppliers.map((s) => (
                                                    <option key={s.id} value={s.name}>{s.name}{s.city ? ` · ${s.city}` : ""}</option>
                                                ))}
                                            </optgroup>
                                        )}
                                    </select>
                                ) : (
                                    <input
                                        value={form.supplier}
                                        onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))}
                                        data-testid="purchase-supplier"
                                        placeholder="Opzionale"
                                        className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold"
                                    />
                                )}
                                {preferred.length === 0 && suppliers.length > 0 && supplierMode === "preferred" && (
                                    <div className="mt-1 text-[10px] text-brand-muted">
                                        Nessun preferito impostato. Marca i tuoi fornitori con ★ in <strong>Fornitori</strong> per avere il dropdown filtrato.
                                    </div>
                                )}
                            </div>
                            <div>
                                <Label>Data</Label>
                                <input
                                    type="date"
                                    value={form.date}
                                    onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                                    data-testid="purchase-date"
                                    className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold"
                                />
                            </div>
                        </div>

                        <button
                            onClick={save}
                            disabled={saving}
                            data-testid="purchase-save"
                            className="mt-2 w-full rounded-full bg-brand-navy text-white py-3 text-sm font-semibold hover:bg-brand-navyLight transition disabled:opacity-60"
                        >
                            {saving ? "Salvataggio…" : "Registra acquisto"}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            <IngredientPicker
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                ingredients={ingredients}
                onPick={(i) => {
                    setSelected(i);
                    setForm((f) => ({ ...f, unit: i.unit }));
                }}
            />
        </>
    );
}

function Label({ children }) {
    return <div className="eyebrow text-brand-ink/60 mb-1.5">{children}</div>;
}
